import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { signOut, fetchAuthSession } from 'aws-amplify/auth';
import { 
  Menu, X, Home, Gamepad, Scroll, MessageSquare, 
  User, LogIn, LogOut, Settings, Search, Book, 
  PlusCircle, Shield, ChevronDown 
} from 'lucide-react';
import './Navigation.css';

const Navigation = ({ user, setUser }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isCreator, setIsCreator] = useState(false);
  const [showAdminMenu, setShowAdminMenu] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    checkUserGroups();
  }, [user]);

  useEffect(() => {
    const closeMenu = () => {
      setIsOpen(false);
      setShowAdminMenu(false);
      setShowUserMenu(false);
    };
    window.addEventListener('resize', closeMenu);

    // Close menus when clicking outside
    const handleClickOutside = (event) => {
      // For user menu
      const userMenu = document.querySelector('.navbar__user-menu');
      if (userMenu && !userMenu.contains(event.target)) {
        setShowUserMenu(false);
      }

      // For admin menu
      const adminMenu = document.querySelector('.navbar__admin-menu');
      if (adminMenu && !adminMenu.contains(event.target)) {
        setShowAdminMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      window.removeEventListener('resize', closeMenu);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const checkUserGroups = async () => {
    try {
      if (!user) {
        setIsAdmin(false);
        setIsCreator(false);
        return;
      }
      
      const session = await fetchAuthSession();
      const groups = session.tokens.accessToken.payload['cognito:groups'] || [];
      
      setIsAdmin(groups.includes('admin'));
      setIsCreator(groups.includes('creator') || groups.includes('admin'));
    } catch (error) {
      console.error('Error checking user groups:', error);
      setIsAdmin(false);
      setIsCreator(false);
    }
  };

  const handleSignOut = async () => {
    try {
      await signOut();
      setUser(null);
      setIsAdmin(false);
      setIsCreator(false);
      navigate('/');
    } catch (error) {
      console.error('Error signing out: ', error);
    }
  };

  const toggleMenu = () => setIsOpen(!isOpen);
  const toggleSearch = () => setShowSearch(!showSearch);
  const toggleAdminMenu = () => setShowAdminMenu(!showAdminMenu);
  const toggleUserMenu = () => setShowUserMenu(!showUserMenu);

  const isActive = (path) => location.pathname === path;

  return (
    <nav className="navbar">
      <div className="navbar__container">
        <Link to="/" className="navbar__logo">ChoiceRealms</Link>
        <div className={`navbar__menu ${isOpen ? 'active' : ''}`}>
          <Link 
            to="/" 
            className={`nav-link ${isActive('/') ? 'active' : ''}`} 
            onClick={toggleMenu}
          >
            <Home size={18} /> Home
          </Link>
          <Link 
            to="/games" 
            className={`nav-link ${isActive('/games') ? 'active' : ''}`} 
            onClick={toggleMenu}
          >
            <Gamepad size={18} /> Games
          </Link>
          <Link 
            to="/tavern" 
            className={`nav-link ${isActive('/tavern') ? 'active' : ''}`} 
            onClick={toggleMenu}
          >
            <MessageSquare size={18} /> Tavern
          </Link>
          <Link 
            to="/blog" 
            className={`nav-link ${isActive('/blog') ? 'active' : ''}`} 
            onClick={toggleMenu}
          >
            <Scroll size={18} /> Blog
          </Link>
          
          {user && (
            <Link 
              to="/characters" 
              className={`nav-link ${isActive('/characters') ? 'active' : ''}`} 
              onClick={toggleMenu}
            >
              <User size={18} /> Characters
            </Link>
          )}

          {(isCreator || isAdmin) && (
            <div className="navbar__admin-menu">
              <button 
                className={`nav-link admin-toggle ${showAdminMenu ? 'active' : ''}`}
                onClick={toggleAdminMenu}
              >
                <Shield size={18} /> Admin <ChevronDown size={14} />
              </button>
              <div className={`navbar__admin-dropdown ${showAdminMenu ? 'show' : ''}`}>
                <Link
                  to="/modules"
                  className="nav-link"
                  onClick={() => {
                    toggleAdminMenu();
                    toggleMenu();
                  }}
                >
                  <Book size={18} /> Modules
                </Link>
                <Link
                  to="/modules/create"
                  className="nav-link"
                  onClick={() => {
                    toggleAdminMenu();
                    toggleMenu();
                  }}
                >
                  <PlusCircle size={18} /> Create Module
                </Link>
                {isAdmin && (
                  <>
                    <Link
                      to="/content-management"
                      className="nav-link"
                      onClick={() => {
                        toggleAdminMenu();
                        toggleMenu();
                      }}
                    >
                      <Settings size={18} /> Management
                    </Link>
                    <Link
                      to="/admin/modules"
                      className="nav-link"
                      onClick={() => {
                        toggleAdminMenu();
                        toggleMenu();
                      }}
                    >
                      <Shield size={18} /> Module Admin
                    </Link>
                  </>
                )}
              </div>
            </div>
          )}
        </div>

        <div className="navbar__actions">
          <button 
            className="navbar__search-toggle" 
            onClick={toggleSearch}
            aria-label="Toggle search"
          >
            <Search size={18} />
          </button>

          {user ? (
            <div className="navbar__user-menu">
              <button
                className="navbar__user-toggle"
                onClick={toggleUserMenu}
              >
                <User size={18} /> {user.username}
              </button>
              <div className={`navbar__user-dropdown ${showUserMenu ? 'show' : ''}`}>
                <Link
                  to="/account"
                  className="nav-link"
                  onClick={() => {
                    toggleUserMenu();
                    toggleMenu();
                  }}
                >
                  Profile
                </Link>
                <Link
                  to="/characters"
                  className="nav-link"
                  onClick={() => {
                    toggleUserMenu();
                    toggleMenu();
                  }}
                >
                  Characters
                </Link>
                {(isCreator || isAdmin) && (
                  <Link
                    to="/modules"
                    className="nav-link"
                    onClick={() => {
                      toggleUserMenu();
                      toggleMenu();
                    }}
                  >
                    My Modules
                  </Link>
                )}
                <button
                  onClick={() => {
                    handleSignOut();
                    toggleUserMenu();
                  }}
                  className="nav-link"
                >
                  <LogOut size={18} /> Sign Out
                </button>
              </div>
            </div>
          ) : (
            <Link 
              to="/auth" 
              className="nav-link auth-button" 
              onClick={toggleMenu}
            >
              <LogIn size={18} /> Sign In
            </Link>
          )}

          <button 
            className="navbar__toggle" 
            onClick={toggleMenu}
            aria-label="Toggle menu"
          >
            {isOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {showSearch && (
        <div className="navbar__search">
          <input 
            type="text" 
            placeholder="Search..." 
            aria-label="Search"
          />
          <button 
            onClick={toggleSearch}
            aria-label="Close search"
          >
            <X size={18} />
          </button>
        </div>
      )}
    </nav>
  );
};

export default Navigation;